import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  createStyles,
  Divider,
  Grid,
  Link,
  makeStyles,
  MenuItem,
  Paper,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';

import {
  RoomType,
  RoomRate,
  DailyRate,
  Property,
  FormStatus,
  roomRateQuery,
  roomRateService,
  PropertyPolicy,
  OptionalAmenity,
  Reservation,
  SleepSchedule,
} from '@lib/state';
import {
  CurrencyDisplay,
  DateDisplay,
  AddressDisplay,
  Section,
  SectionVariant,
  LoadingIndicator,
  formatCurrency,
  Analytics,
  HousekeepingPolicyDisplay,
} from '../../components';
import { FormCheckbox, FormTextField, ErrorDisplay } from '../../forms';
import { useLookup, useObservable } from '../../utils';
import { RulesAndRestrictionsModal } from '../rules-and-restrictions.modal';
import { useCommonStyle } from '../../commonStyle';
import { LocationFormFields } from './location-form';
import { ReservationStatus, RoomHoldStatus } from '@lib/state/api/generated/reservations';

import { RoomHoldsModal } from '../room-holds.modal';

// Keep this aligned with backend PropertyConfiguration.DefaultTouchUpCleanFrequency.
const DEFAULT_TOUCH_UP_CLEAN_FREQUENCY = 4;

const useStyles = makeStyles(theme =>
  createStyles({
    // add style for bold
    bold: {
      fontWeight: 'bold',
    },
    removePromo: {
      color: theme.palette.error.main,
      cursor: 'pointer',
      marginLeft: '0.5rem',
    },
    relative: {
      position: 'relative',
    },
    loadingContainer: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 15,
      backgroundColor: theme.palette.action.disabled,
    },
  })
);

interface FormValues extends LocationFormFields {
  flexRate: boolean;
  dailyHousekeeping: boolean;
  room: {
    roomTypeId: string;
    dailyRates: Array<DailyRate>;
  };
  amenities: Array<OptionalAmenity>;
}

interface EnabledRoomRateFormFeature {
  enabledFeatures?: {
    selectRoomType?: boolean;
    changeDates?: boolean;
    changeRoomQuantity?: boolean;
    allowPromoCode?: boolean;
    addFlexibility?: boolean;
    addAmenities?: boolean;
    addDailyHousekeeping?: boolean;
  };
}

interface Props extends EnabledRoomRateFormFeature {
  //Component props
  className?: string;
  properties: Array<Property>;
  usePaper?: boolean | undefined;
  //Reservation detail props
  reservation?: Reservation;
  quantity: number;
  roomType?: RoomType;
  selectedRate?: RoomRate;
  promo?: string | null;
  policy?: PropertyPolicy;
  //Form props
  status: FormStatus;
  variant?: SectionVariant;
  onEditLocation: () => void;
  onLoadFail: () => void;
  setRequiresPayment: React.Dispatch<React.SetStateAction<boolean>>;
  availabilityError?: Error;
  multipleCorpAccts?: boolean;
  isLoading?: boolean;
  isSingle?: boolean;
}

export const RoomRateForm: React.FC<Props> = ({
  //Component props
  className,
  properties,
  usePaper,
  variant,
  //Reservation props
  reservation,
  quantity = 0,
  roomType,
  selectedRate,
  promo = '',
  policy,
  //Form props
  status,
  onEditLocation,
  onLoadFail,
  setRequiresPayment,
  enabledFeatures = {
    selectRoomType: true,
    changeDates: true,
    changeRoomQuantity: true,
  },
  availabilityError,
  multipleCorpAccts,
  isSingle = true,
  isLoading,
}) => {
  const { removePromo, relative, loadingContainer, bold } = useStyles();
  const styles = useCommonStyle();

  const [openRoomHoldsModal, setOpenRoomHoldsModal] = useState(false);

  const [editPromoCode, setEditPromoCode] = useState(false);
  const [didSubmitPromo, setDidSubmitPromo] = useState<string | undefined>(undefined);
  const corporateBooking = useObservable(roomRateQuery.corporateBooking);
  const changeBillingRefunds = useObservable(roomRateQuery.changeBillingRefunds);
  const propertyLookup = useLookup(properties, x => x.id);
  const { register, watch, setValue } = useFormContext<FormValues>();

  useEffect(() => {
    register({ name: 'room.roomTypeId' });
    register({ name: 'room.dailyRates' });
  }, []);

  const promoCode = watch('promoCode', promo);
  const dailyHousekeeping = watch('dailyHousekeeping');

  const {
    propertyId,
    checkInDate,
    checkOutDate,
    flexRate,
    room,
    quantity: roomQuantity,
    amenities = [],
  } = watch({
    nest: true,
  });

  const hasCorporateHousekeeping = !!corporateBooking?.housekeeping;
  const isZeroHousekeepingRate = selectedRate?.totalHousekeepingRate === 0;
  const isDefaultCorporateFrequency =
    corporateBooking?.housekeeping?.touchUpCleanFrequency === DEFAULT_TOUCH_UP_CLEAN_FREQUENCY;

  const housekeepingIncluded = useMemo(() => hasCorporateHousekeeping || isZeroHousekeepingRate, [
    hasCorporateHousekeeping,
    isZeroHousekeepingRate,
  ]);

  // Corporate cleaning at the property default frequency is a free perk;
  // any other corporate frequency is mandatory but billed per night.
  const housekeepingFreeOfCharge = useMemo(
    () => isDefaultCorporateFrequency || isZeroHousekeepingRate,
    [isDefaultCorporateFrequency, isZeroHousekeepingRate]
  );

  const housekeepingDefaultChecked = useMemo(
    () => !!reservation?.hasHousekeeping || housekeepingIncluded,
    [reservation, housekeepingIncluded]
  );

  useEffect(() => {
    setValue('room', {
      roomTypeId: selectedRate?.roomTypeId,
      dailyRates: selectedRate?.dailyRates,
    });

    // Forcing react-hook-form to run validation for the next
    // two setValue calls is needed for the form to re-render

    // Default flex to true when flex rate is 0.
    setValue(
      'flexRate',
      !!reservation?.changeable || flexRate || selectedRate?.totalFlexRate === 0,
      true
    );

    if (selectedRate?.dailyRates.length ?? 0 > 1) {
      // Default dailyHousekeeping to true when corporateBooking has
      // housekeeping overrides or housekeeping rate is 0.
      setValue('dailyHousekeeping', housekeepingDefaultChecked || !!dailyHousekeeping, true);
    }
  }, [corporateBooking, reservation, housekeepingDefaultChecked, selectedRate]);

  const amenityLookup = useLookup(selectedRate?.amenities, x => x.code);

  const includedAmenities = useMemo(() => selectedRate?.amenities.filter(x => x.isIncluded), [
    selectedRate,
  ]);
  const optionalAmenities = useMemo(() => selectedRate?.amenities.filter(x => !x.isIncluded), [
    selectedRate,
  ]);

  const housekeeping = useMemo(
    () => corporateBooking?.housekeeping ?? { touchUpCleanFrequency: dailyHousekeeping ? 1 : null },
    [corporateBooking, dailyHousekeeping]
  );
  const roomsWatch = watch('rooms');
  const guestTbd = watch('guestTbd');

  // determines the amount of late checkouts
  // for an existing reservation, in the corporate model, each reservation will only have one room
  const hasAutomaticLateCheckout = useMemo(() => {
    return !!reservation?.hasAutomaticLateCheckout;
  }, [reservation]);

  // for a new booking, this amount is determined by the checking the sleep schedule of each room
  const lateCheckouts = useMemo(() => {
    if (roomsWatch instanceof Array)
      return roomsWatch.filter(function ({ sleepSchedule }) {
        return sleepSchedule && sleepSchedule === SleepSchedule.DaySleeper;
      }).length;

    return null;
  }, [roomsWatch]);

  const {
    grandAmount,
    grandTaxesAndFees,
    grandTotal,
    grandChangeBillingRefundsTotal,
    grandAmenities,
    ...totals
  } = useMemo(() => {
    // null values for totals prevents requiresPayment toggling which
    // can lead to rapid form swapping and react-hook-form errors
    const totals: {
      amount: number;
      fees: number;
      amenities: number;
      tax: number;
      taxesAndFees: number | null;
      total: number | null;
      totalLateCheckoutFees: number | null;
      grandAmount: number | null;
      grandTaxesAndFees: number | null;
      grandTotal: number | null;
      grandChangeBillingRefundsTotal: number | null;
      grandAmenities: number | null;
      quantity: number;
    } = {
      amount: 0,
      fees: 0,
      amenities: 0,
      tax: 0,
      taxesAndFees: null,
      total: null,
      totalLateCheckoutFees: null,
      grandAmount: null,
      grandTaxesAndFees: null,
      grandTotal: null,
      grandChangeBillingRefundsTotal: null,
      grandAmenities: null,
      quantity: 1,
    };

    if (!selectedRate || !quantity) return totals;

    const useSalesTaxForMealPlan =
      properties.find(p => p.id === propertyId)?.configuration?.useSalesTaxForMealCosts ?? true;
    const { state, county, city } = selectedRate.taxRates;
    const { state: salesState, county: salesCounty, city: salesCity } = selectedRate.salesTaxRates;
    const lastStayDate = selectedRate.dailyRates[selectedRate.dailyRates.length - 1];

    function calcTotalLateCheckoutFees() {
      if (hasAutomaticLateCheckout && selectedRate) return selectedRate.lateCheckOutFee * 1;

      if (corporateBooking?.isAutomaticLateCheckoutEnabled && lateCheckouts && selectedRate) {
        return selectedRate.lateCheckOutFee * lateCheckouts;
      }
      return null;
    }

    totals.totalLateCheckoutFees = calcTotalLateCheckoutFees();

    function calcTax(amount: number, isSalesTaxes: boolean) {
      return !isSalesTaxes
        ? Math.round(amount * (state + county + city)) / 100
        : Math.round(amount * (salesState + salesCounty + salesCity)) / 100;
    }

    let refundTotal: number = 0;

    for (const day of selectedRate.dailyRates) {
      let amount = day.standardRate;

      const includeChangeBillingRefunds: boolean =
        !!changeBillingRefunds &&
        !!changeBillingRefunds.refundedStayDates &&
        !!changeBillingRefunds.refundedStayDates.find(
          x => new Date(x).toLocaleDateString() === day.date.toLocaleDateString()
        );

      if (flexRate) {
        amount += day.flexRate;
      }
      if (dailyHousekeeping && !housekeepingFreeOfCharge && day !== lastStayDate) {
        amount += day.housekeepingRate;
      }

      totals.amount += amount;
      const tax = calcTax(amount, false);
      totals.tax += tax;

      if (includeChangeBillingRefunds) {
        refundTotal += amount;
        refundTotal += tax;
      }
    }

    for (const amenity of amenities) {
      if (!amenity.selected) continue;

      //Need a better way to do this
      const isMealPlan = amenity.code === 'meals-all';

      const amenityFee = amenityLookup.get(amenity.code)?.totalFee ?? 0;

      totals.amenities += amenityFee;
      totals.tax += calcTax(amenityFee, !isMealPlan ? true : useSalesTaxForMealPlan);

      if (
        changeBillingRefunds?.refundedStayDates &&
        changeBillingRefunds.refundedStayDates.length > 0
      ) {
        const amenityRefund =
          (amenityFee / selectedRate.dailyRates.length) *
          changeBillingRefunds.refundedStayDates.length;
        refundTotal += amenityRefund;
        const amenityRefundTax = calcTax(amenityRefund, true);
        refundTotal += amenityRefundTax;
      }
    }

    const housekeepingFee = selectedRate.housekeepingFee;
    const housekeepingFeeTax = calcTax(housekeepingFee, false);
    totals.fees = housekeepingFee;
    totals.tax += housekeepingFeeTax;
    if (reservation?.status === ReservationStatus.Canceled) {
      refundTotal += housekeepingFee;
      refundTotal += housekeepingFeeTax;
    }

    totals.quantity = !guestTbd ? quantity : quantity - 1;

    totals.taxesAndFees = totals.fees + totals.tax;
    totals.total = totals.fees + totals.amenities + totals.amount + totals.tax;

    totals.grandAmount = totals.amount * totals.quantity;
    totals.grandAmenities = totals.amenities * totals.quantity;
    totals.grandTaxesAndFees = totals.taxesAndFees * totals.quantity;
    totals.grandTotal = totals.total * totals.quantity;

    if (totals.totalLateCheckoutFees) {
      let lateCheckOutTax = calcTax(totals.totalLateCheckoutFees, false);
      totals.grandTotal += totals.totalLateCheckoutFees + lateCheckOutTax;
      totals.grandTaxesAndFees += totals.totalLateCheckoutFees + lateCheckOutTax;
    }

    if (refundTotal > 0) {
      totals.grandChangeBillingRefundsTotal = -1 * refundTotal;
      totals.grandTotal += totals.grandChangeBillingRefundsTotal;
    }

    return totals;
  }, [
    reservation,
    selectedRate,
    flexRate,
    dailyHousekeeping,
    amenities,
    amenityLookup,
    quantity,
    corporateBooking,
    changeBillingRefunds,
    lateCheckouts,
    hasAutomaticLateCheckout,
  ]);

  useEffect(() => {
    setRequiresPayment(grandTotal !== 0);
  }, [grandTotal, setRequiresPayment]);

  const applyPromoCode = useCallback(() => {
    roomRateService.updateUI({ promoCode: promoCode ?? undefined });
    setDidSubmitPromo(promoCode ?? undefined);
  }, [promoCode]);

  const removePromoCode = useCallback(() => {
    setValue('promoCode', '');

    roomRateService.updateUI({ promoCode: undefined });
    setDidSubmitPromo(undefined);
    setEditPromoCode(false);
  }, [promoCode, setValue]);

  useEffect(() => {
    const isChecked = selectedRate?.roomHoldStatus === RoomHoldStatus.Required;
    setValue('useRoomHolds', isChecked);
  }, [selectedRate?.roomHoldStatus, setValue]);

  const setQuantity = useCallback(
    (quantity: string) => {
      roomRateService.updateUI({ quantity: parseInt(quantity) });
      setValue('quantity', parseInt(quantity));
    },
    [setValue]
  );

  const { errors } = useFormContext();
  const fieldRef = React.createRef<any>();

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0 && fieldRef.current) {
      window.scrollTo({ behavior: 'auto', top: fieldRef.current?.offsetTop });
    }
  }, [errors, fieldRef.current]);

  useEffect(() => {
    if ((!propertyId || !roomType || !selectedRate) && !isLoading && onLoadFail) onLoadFail();
  }, [propertyId, roomType, selectedRate, isLoading, onLoadFail]);

  if (!propertyId || !checkInDate || !checkOutDate || !roomType || !selectedRate || !roomQuantity) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Analytics
        {...{
          dailyHousekeeping: dailyHousekeeping,
          total: grandTotal != null ? formatCurrency(grandTotal) : undefined,
          flex: flexRate,
          promoCode: selectedRate.isPromotional === true ? promoCode : undefined,
          isCorporateBooking: !!corporateBooking,
          corporateName: corporateBooking?.name,
        }}
      />
      <Section
        className={clsx(className, relative)}
        title="Customize Your Stay"
        maxWidth="md"
        variant={variant}
        data-testid="customizeStayDisplay"
      >
        <Paper className={clsx({ [styles.paper]: usePaper, [styles.notPaper]: !usePaper })} square>
          {status === FormStatus.Pending && (
            <div className={loadingContainer}>
              <LoadingIndicator loadingText="" fillContainer />
            </div>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <AddressDisplay
              addressVariant="cityState"
              variant="subtitle1"
              {...propertyLookup.get(propertyId)?.location}
              data-testid="customizeStayAddressDisplay"
            />
            {!!enabledFeatures?.changeDates && (
              <Link
                component="button"
                type="button"
                color="primary"
                onClick={onEditLocation}
                variant="body2"
                name="changeDates"
                data-testid="customizeStayChangeDatesButton"
              >
                <small>Change Dates</small>
              </Link>
            )}
          </Box>
          <ErrorDisplay error={availabilityError} />
          <Grid container spacing={2}>
            <Grid item sm={3} xs={6}>
              <Typography className={styles.dense} variant="overline">
                <strong># of Rooms</strong>
              </Typography>
              {!enabledFeatures?.changeRoomQuantity ? (
                <Typography className={styles.dense} variant="body2">
                  {roomQuantity}
                </Typography>
              ) : (
                <FormTextField
                  name="quantity"
                  color="primary"
                  value={roomQuantity ?? '1'}
                  onChange={e => {
                    setQuantity(e.target.value);
                  }}
                  select
                  size="small"
                  controlled
                  fullWidth
                  data-testid="customizeStayQuantity"
                >
                  {[...Array(25)].map((x, i) => (
                    <MenuItem key={i} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </FormTextField>
              )}
            </Grid>
            <Grid item sm={3} xs={6}>
              <Typography className={styles.dense} variant="overline">
                <strong>Check-In</strong>
              </Typography>
              <DateDisplay
                className={styles.dense}
                variant="body2"
                date={checkInDate}
                dateFormat="MM/dd/yyyy"
                data-testid="customizeStayCheckInDateDisplay"
              />
            </Grid>
            <Grid item sm={3} xs={6}>
              <Typography className={styles.dense} variant="overline">
                <strong>Check-Out</strong>
              </Typography>
              <DateDisplay
                className={styles.dense}
                variant="body2"
                date={checkOutDate}
                dateFormat="MM/dd/yyyy"
                data-testid="customizeStayCheckOutDateDisplay"
              />
            </Grid>
            <Grid item sm={3} xs={6}>
              <Typography className={styles.dense} variant="overline">
                <strong>Room Type</strong>
              </Typography>
              <Typography
                className={styles.dense}
                variant="body2"
                data-testid="customizeStayRoomTypeDisplay"
              >
                {roomType.name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              {corporateBooking && selectedRate.roomHoldStatus !== RoomHoldStatus.Unavailable && (
                <Box display="flex" alignItems="center">
                  <FormCheckbox
                    name="useRoomHolds"
                    label={<Typography variant="body2">Use Corporate Room Holds</Typography>}
                    disabled={selectedRate.roomHoldStatus === RoomHoldStatus.Required}
                  />

                  <Link onClick={() => setOpenRoomHoldsModal(true)}>Show Availability</Link>
                  <RoomHoldsModal
                    propertyId={propertyId}
                    open={openRoomHoldsModal}
                    roomTypeId={roomType?.id}
                    corporateAccount={corporateBooking}
                    checkIn={checkInDate}
                    checkOut={checkOutDate}
                    onClose={() => setOpenRoomHoldsModal(false)}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          <Box mt={1} mb={0.5}>
            <Divider />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography className={styles.dense} variant="subtitle2">
              Room Charges
            </Typography>

            <Box display="flex" alignItems="center" flexDirection="column">
              <CurrencyDisplay
                value={selectedRate.averageStandardRate}
                className={clsx(styles.dense, bold)}
                variant="body2"
              >
                /night
              </CurrencyDisplay>
            </Box>
          </Box>

          {selectedRate.isCorporate && corporateBooking && (
            <Box display="flex" dir="row" alignItems="center">
              <Typography
                className={styles.dense}
                variant="body2"
                data-testid="roomRateCorporateBookingDisplay"
                style={{
                  display: 'flex',
                }}
              >
                Using corporate booking rates for corporate account &nbsp;
                <strong>{corporateBooking.name}</strong>
              </Typography>
              {multipleCorpAccts && (
                <Link
                  component="button"
                  color="primary"
                  onClick={onEditLocation}
                  variant="body2"
                  name="changeDates"
                  data-testid="customizeStayChangeDatesButton"
                  style={{
                    margin: '0 0.5rem',
                  }}
                >
                  <small>(change account)</small>
                </Link>
              )}
            </Box>
          )}

          <Box>
            {selectedRate.isPromotional && (
              <Typography className={styles.dense} variant="caption">
                <small>Promotional rate applied.</small>
                <small className={removePromo} onClick={removePromoCode}>
                  (Remove)
                </small>
              </Typography>
            )}

            {!selectedRate.isPromotional &&
              (!enabledFeatures?.allowPromoCode || enabledFeatures.allowPromoCode === true) && (
                <>
                  {editPromoCode ? (
                    <>
                      <Box display="flex" alignItems="flex-end">
                        <FormTextField
                          name="promoCode"
                          label="Promo Code"
                          defaultValue=""
                          size="small"
                          data-testid="customizeStayPromoCodeInput"
                        />
                        <Box display="inline-flex" alignItems="center" ml={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={applyPromoCode}
                            name="applyPromoCode"
                            disabled={!promoCode || status === FormStatus.Pending}
                            data-testid="customizeStayPromoCodeApplyButton"
                          >
                            Apply
                          </Button>
                          <Box ml={2}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              onClick={() => setEditPromoCode(false)}
                              data-testid="customizePromoCodeCancelButton"
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      {didSubmitPromo &&
                        (status === FormStatus.Initial || status === FormStatus.Success) && (
                          <Typography color="error">
                            <small>Code is invalid or cannot be applied to this reservation.</small>
                          </Typography>
                        )}
                    </>
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      color="primary"
                      onClick={() => setEditPromoCode(true)}
                      variant="body2"
                      data-testid="customizeStayPromoCodeButton"
                    >
                      <small>Have a promotion or corporate booking code?</small>
                    </Link>
                  )}
                </>
              )}
          </Box>

          <Box mt={0.5} mb={1}>
            <Divider />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography className={styles.dense} variant="subtitle2">
              Booking Flexibility
            </Typography>
            {flexRate && (
              <>
                {selectedRate.totalFlexRate > 0 ? (
                  <CurrencyDisplay
                    className={styles.dense}
                    variant="body2"
                    addon
                    value={selectedRate.averageFlexRate}
                  >
                    /night
                  </CurrencyDisplay>
                ) : (
                  <Typography className={styles.dense} variant="body2">
                    Included
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Typography className={styles.dense} variant="caption" display="block">
            {flexRate ? (
              <small>
                Reservation refundable up to {Math.round(selectedRate.cancellationWindow / 60)} hrs
                before check-in.
              </small>
            ) : (
              <small>Reservation is currently NONREFUNDABLE.</small>
            )}
          </Typography>
          <FormCheckbox
            className={clsx({ [styles.hidden]: selectedRate.totalFlexRate === 0 })}
            name="flexRate"
            label={<Typography variant="body2">Add booking flexibility</Typography>}
            fullWidth
            defaultChecked={!!reservation?.changeable || selectedRate.totalFlexRate === 0}
            data-testid="customizeStayFlexRateCheck"
            disabled={enabledFeatures?.addFlexibility === false}
          />
          <Box my={1}>
            <Divider />
          </Box>

          {room?.dailyRates?.length > 1 && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography className={styles.dense} variant="subtitle2">
                  Housekeeping
                </Typography>
                {dailyHousekeeping && (
                  <>
                    {housekeepingIncluded ? (
                      <Typography className={styles.dense} variant="body2">
                        Included
                      </Typography>
                    ) : (
                      <CurrencyDisplay
                        className={styles.dense}
                        variant="body2"
                        addon
                        value={selectedRate.averageHousekeepingRate}
                      >
                        /night
                      </CurrencyDisplay>
                    )}
                  </>
                )}
              </Box>

              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography className={styles.dense} variant="caption" display="block">
                  <small>
                    <HousekeepingPolicyDisplay {...housekeeping} />
                  </small>
                </Typography>
                {dailyHousekeeping && (
                  <Typography className={styles.dense} variant="caption" display="block">
                    <small>For {selectedRate.dailyRates.length - 1} nights.</small>
                  </Typography>
                )}
              </Box>
              <FormCheckbox
                className={clsx({
                  [styles.hidden]: housekeepingIncluded,
                })}
                name="dailyHousekeeping"
                label={<Typography variant="body2">Add daily housekeeping</Typography>}
                fullWidth
                defaultChecked={housekeepingDefaultChecked}
                data-testid="customizeStayHousekeepingCheck"
                disabled={enabledFeatures?.addDailyHousekeeping === false}
              />
              <Box my={1}>
                <Divider />
              </Box>
            </>
          )}

          {selectedRate.amenities.length > 0 && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography className={styles.dense} variant="subtitle2">
                  Amenities & Add-Ons
                </Typography>
                {amenities.some(x => x.selected) && (
                  <CurrencyDisplay
                    className={styles.dense}
                    variant="body2"
                    addon
                    value={amenities
                      .filter(x => x.selected)
                      .reduce((sum, x) => sum + (amenityLookup.get(x.code)?.averageFee ?? 0), 0)}
                  >
                    /night
                  </CurrencyDisplay>
                )}
              </Box>
              <Typography className={styles.dense} variant="caption" display="block">
                {!!includedAmenities?.length ? (
                  <small>Your stay includes: {includedAmenities.map(x => x.name).join(', ')}</small>
                ) : (
                  <small>Select from the options below.</small>
                )}
              </Typography>
              {optionalAmenities?.map((amenity, i) => (
                <React.Fragment key={amenity.code}>
                  <FormTextField
                    type="hidden"
                    name={`amenities.[${i}].code`}
                    defaultValue={amenity.code}
                  />
                  <FormCheckbox
                    name={`amenities[${i}].selected`}
                    label={<Typography variant="body2">{amenity.name}</Typography>}
                    fullWidth
                    defaultChecked={!!amenity.selected}
                    disabled={enabledFeatures?.addAmenities === false}
                  />
                </React.Fragment>
              ))}
              <Box mt={1} mb={0.5}>
                <Divider />
              </Box>
            </>
          )}
          {(totals.quantity > 1 || !isSingle) && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Per {isSingle ? 'room' : 'guest'} charges</Typography>
                <CurrencyDisplay value={totals.amount} variant="body2" />
              </Box>
              {totals.amenities > 0 && (
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    Per {isSingle ? 'room' : 'guest'} amenity & add-on charges
                  </Typography>
                  <CurrencyDisplay value={totals.amenities} variant="body2" />
                </Box>
              )}
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  Per {isSingle ? 'room' : 'guest'} taxes and fees
                </Typography>
                <CurrencyDisplay value={totals.taxesAndFees} variant="body2" />
              </Box>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">
                  Requested # of {isSingle ? 'rooms' : 'guests'}
                </Typography>
                <Typography variant="body2">{totals.quantity}</Typography>
              </Box>
              {!isSingle && (
                <Box>
                  <Typography variant="caption" style={{ paddingLeft: 5 }}>
                    Additional charges may apply for partially occupied rooms
                  </Typography>
                </Box>
              )}

              <Box my={0.5}>
                <Divider />
              </Box>
            </>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">Total room charges</Typography>
            <CurrencyDisplay value={grandAmount} variant="body2" />
          </Box>
          {!!grandAmenities && grandAmenities > 0 && (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Total amenity &amp; add-on charges</Typography>
              <CurrencyDisplay value={grandAmenities} variant="body2" />
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">Total taxes and fees</Typography>
            <CurrencyDisplay value={grandTaxesAndFees} variant="body2" />
          </Box>
          {!!grandChangeBillingRefundsTotal && (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Total refunds</Typography>
              <CurrencyDisplay value={grandChangeBillingRefundsTotal} variant="body2" />
            </Box>
          )}

          <Box my={0.5}>
            <Divider />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Total for stay</Typography>
            <CurrencyDisplay
              value={grandTotal}
              variant="subtitle1"
              data-testid="customizeStayTotalDisplay"
            />
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <div ref={fieldRef}>
              <FormCheckbox
                name="acceptPolicy"
                label={
                  <Typography variant="body2">
                    I agree to the <RulesAndRestrictionsModal policy={policy} />
                  </Typography>
                }
                validationOptions={{
                  validate: {
                    accepted: value => value || 'Rules and restrictions must be accepted.',
                  },
                }}
                helperText={errors => errors['acceptPolicy']?.message}
                data-testid="customizeStayAcceptPolicyCheck"
              />
            </div>
          </Box>
        </Paper>
      </Section>
    </>
  );
};
